import React, { createContext, useContext, useState, useEffect } from 'react';

// Export the context for use with Context.Consumer in class components
export const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation dictionaries
const translations = {
  en: {
    // Navigation
    back: 'Back',
    settings: 'Settings',
    profileMenu: 'Profile menu',
    logout: 'Logout',
    switchAccount: 'Switch Account',

    // Landing Page
    appName: 'iGo',
    signInWithGoogle: 'Sign in with Google',
    planYourPerfect: 'Plan Your Perfect',
    journey: 'Journey',
    tagline: 'AI-powered trip planning with automatic email parsing, smart itineraries, and real-time collaboration',
    builtWith: 'Built with ❤️ on Emergent',

    // Features
    smartEmailParsing: 'Smart Email Parsing',
    smartEmailParsingDesc: 'Automatically extract bookings from Gmail confirmations using AI',
    aiItineraryBuilder: 'AI Itinerary Builder',
    aiItineraryBuilderDesc: 'Get personalized travel suggestions based on your destination and preferences',
    interactiveMaps: 'Interactive Maps',
    interactiveMapsDesc: 'Visualize your entire trip on Google Maps with route planning',
    timelineView: 'Timeline View',
    timelineViewDesc: 'Organize hotels, flights, activities, and restaurants in a beautiful timeline',
    shareCollaborate: 'Share & Collaborate',
    shareCollaborateDesc: 'Share trips with travel companions and plan together in real-time',
    budgetTracking: 'Budget Tracking',
    budgetTrackingDesc: 'Keep track of expenses and stay within your travel budget',

    // Dashboard
    welcomeBack: 'Welcome back',
    readyToPlanner: 'Ready to plan your next adventure?',
    createNewTrip: 'Create New Trip',
    noTripsYet: 'No trips yet',
    createFirstTrip: 'Create your first trip to get started!',
    loadingTrips: 'Loading trips...',

    // Trip Detail
    shareTrip: 'Share Trip',
    editTrip: 'Edit Trip',
    aiEmailParse: 'AI Email Parse',
    gmailIntegration: 'Gmail Integration',
    exportToCalendar: 'Export to Calendar',
    exportCalendar: 'Calendar',
    calendarExportSuccess: 'Exported to Google Calendar successfully',
    calendarExportFailed: 'Failed to export to calendar',
    noItemsToExport: 'No itinerary items to export',
    connectGmailFirst: 'Please connect Gmail from Dashboard to enable calendar export',
    passPortfolio: 'Pass Portfolio',
    budget: 'Budget',
    used: 'used',
    addItem: 'Add Item',
    parseEmail: 'Import Booking',
    emailsFound: 'Emails Found',
    reviewAndAddToItinerary: 'Review and Add To Itinerary',
    parseAndAdd: 'Parse & Add to Itinerary',
    noEmailsFound: 'No booking emails found',
    bookingsFound: 'Bookings Found',
    selectBookingsToAdd: 'Select which bookings to add to your itinerary',
    adding: 'Adding...',
    noItemsYet: 'No itinerary items yet. Add your first item or parse an email!',

    // Budget
    budgetOverview: 'Budget Overview',
    setBudgetToTrack: 'Set a budget for this trip to see expense tracking',
    lodging: 'Lodging',
    travel: 'Travel',
    dining: 'Dining',
    activities: 'Activities',

    // Settings
    settingsTitle: 'Settings',
    account: 'Account',
    accountDesc: 'Manage your account settings and preferences.',
    notifications: 'Notifications',
    notificationsDesc: 'Control how you receive notifications.',
    privacySecurity: 'Privacy & Security',
    privacySecurityDesc: 'Manage your data and security settings.',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    appearance: 'Appearance',
    appearanceDesc: 'Customize the look and feel of the app.',
    language: 'Language',
    languageDesc: 'Choose your preferred language',
    theme: 'Theme',
    light: 'Light',
    lightThemeDesc: 'Use light theme',
    dark: 'Dark',
    darkThemeDesc: 'Use dark theme',
    system: 'System',
    systemThemeDesc: 'Match device theme',
    comingSoon: 'Coming soon',
    close: 'Close',

    // Account Settings
    profileInfo: 'Profile Information',
    displayName: 'Display Name',
    displayNameDesc: 'This name will be shown to others when you share trips',
    email: 'Email',
    emailDesc: 'Your login email (managed by Google)',
    memberSince: 'Member since',
    preferences: 'Preferences',
    defaultCurrency: 'Default Currency',
    distanceUnit: 'Distance Unit',
    kilometers: 'Kilometers',
    miles: 'Miles',
    temperatureUnit: 'Temperature Unit',
    celsius: 'Celsius',
    fahrenheit: 'Fahrenheit',
    dateFormat: 'Date Format',
    timeFormat: 'Time Format',
    hour12: '12-hour',
    hour24: '24-hour',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    changesSaved: 'Changes saved successfully',
    errorSaving: 'Error saving changes',

    // Notification Settings
    emailNotifications: 'Email Notifications',
    emailNotificationsDesc: 'Receive updates about your trips via email',
    tripReminders: 'Trip Reminders',
    tripRemindersDesc: 'Get reminders before your trips start',
    shareNotifications: 'Share Notifications',
    shareNotificationsDesc: 'Get notified when someone shares a trip with you',
    marketingEmails: 'Marketing Emails',
    marketingEmailsDesc: 'Receive news and promotional offers',

    // Privacy & Security
    dataExport: 'Data Export',
    dataExportDesc: 'Download all your data including trips, itineraries, and preferences',
    exportData: 'Export My Data',
    exporting: 'Exporting...',
    exportSuccess: 'Data exported successfully',
    exportError: 'Error exporting data',
    activeSessions: 'Active Sessions',
    activeSessionsDesc: 'Manage devices and sessions logged into your account',
    currentSession: 'Current Session',
    revokeSession: 'Revoke',
    revokeSessionConfirm: 'Are you sure you want to log out of this session?',
    sessionRevoked: 'Session revoked successfully',
    noOtherSessions: 'No other active sessions',
    dangerZone: 'Danger Zone',
    deleteAccount: 'Delete Account',
    deleteAccountDesc: 'Permanently delete your account and all associated data. This action cannot be undone.',
    deleteAccountConfirm: 'Are you absolutely sure you want to delete your account? This will permanently remove all your trips, itineraries, documents, and preferences. This action CANNOT be undone.',
    typeToConfirm: 'Type DELETE to confirm',
    accountDeleted: 'Account deleted successfully',
    deleteAccountError: 'Error deleting account',
    createdAt: 'Created',
    expiresAt: 'Expires',

    // Common
    from: 'From',
    to: 'To',
    date: 'Date',
    cost: 'Cost',
    location: 'Location',
    notes: 'Notes',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    confirm: 'Confirm',
    optional: '(optional)',
    or: 'OR',
    type: 'Type',
    title: 'Title',

    // Create/Edit Trip Dialog
    createNewTripTitle: 'Create New Trip',
    editTripTitle: 'Edit Trip',
    fillDetails: 'Fill in the details below to create a new trip',
    updateDetails: 'Update your trip details below',
    tripTitle: 'Trip Title',
    tripTitleRequired: 'Trip Title *',
    startingFrom: 'Starting From',
    startingFromRequired: 'Starting From *',
    destinations: 'Destinations',
    destinationsOptional: 'Destinations (Optional)',
    searchLocation: 'Search location (e.g., New York, USA)',
    searchDestination: 'Search destination (e.g., Paris, France)',
    startDate: 'Start Date',
    endDate: 'End Date',
    createTrip: 'Create Trip',
    updateTrip: 'Update Trip',
    deleteConfirm: 'Are you sure you want to delete this trip? This action cannot be undone.',
    tripCreatedSuccess: 'Trip created successfully!',
    tripUpdatedSuccess: 'Trip updated successfully!',
    tripDeletedSuccess: 'Trip deleted successfully!',
    requiredFields: 'Please fill in required fields (Title and Starting Location)',
    sessionExpired: 'Session expired. Please refresh the page.',
    failedToCreateTrip: 'Failed to create trip',
    failedToUpdateTrip: 'Failed to update trip',
    failedToDeleteTrip: 'Failed to delete trip',
    failedToLoadTrips: 'Failed to load trips',
    loadedFromOffline: 'Loaded trips from offline storage',
    sharedWith: 'Shared with',
    person: 'person',
    people: 'people',
    currency: 'Currency',

    // Add/Edit Itinerary Dialog
    addItineraryItem: 'Add Itinerary Item',
    addItineraryDesc: 'Add a new activity, flight, hotel, or other item to your trip itinerary',
    editItineraryItem: 'Edit Itinerary Item',
    editItineraryDesc: 'Update the details of this itinerary item',
    typeHotel: 'Hotel',
    typeFlight: 'Flight',
    typeTrain: 'Train',
    typeVacationRental: 'Vacation Rental',
    typeCruise: 'Cruise',
    typeActivity: 'Activity',
    typeRestaurant: 'Restaurant',
    typeOther: 'Other',
    embarkAt: 'Embark At',
    debarkAt: 'Disembark At',
    pleaseEnterBothPorts: 'Please enter both embark and disembark ports',
    embark: 'Embark',
    debark: 'Disembark',
    disembark: 'Disembark',
    fullRoute: 'Full Route',
    confirmation: 'Confirmation',
    portDeparture: 'Departure',
    portArrival: 'Port of Call',
    searchPortExample: 'Search port (e.g., Port of Miami)',
    searchPortArrival: 'Search port (e.g., Port Canaveral)',
    typeToSearchPorts: 'Type to search cruise ports',
    portsOfCall: 'Ports of Call',
    portsOfCallDesc: 'Add intermediate stops on your cruise',
    addPort: 'Add Port',
    noPortsAdded: 'No ports added yet',
    portName: 'Port Name',
    searchPortPlaceholder: 'e.g., Cozumel, Mexico',
    arrivalDate: 'Arrival',
    departureDate: 'Departure',
    portOfCall: 'Port of Call',
    cruiseLeg: 'Leg',
    of: 'of',
    departingFrom: 'Departing From',
    arrivingAt: 'Arriving At',
    searchAirport: 'Search airport',
    searchAirportExample: 'Search airport (e.g., Los Angeles or LAX)',
    searchAirportArrival: 'Search airport (e.g., New York or JFK)',
    searchStation: 'Search station',
    searchStationExample: 'Search station (e.g., Paris Gare du Nord)',
    searchStationArrival: 'Search station (e.g., London St Pancras)',
    searchLocationExample: 'Search location (e.g., Eiffel Tower, Paris)',
    typeToSearchAirports: 'Type to search airports',
    typeToSearchStations: 'Type to search train stations',
    searchOrEnterLocation: 'Search or enter location',
    startDateTime: 'Start Date/Time',
    endDateTime: 'End Date/Time',
    confirmationNumber: 'Confirmation Number',
    additionalNotes: 'Add any additional notes...',
    additionalDetails: 'Additional details...',
    saveItem: 'Save Item',
    updateItem: 'Update Item',
    itemAddedSuccess: 'Item added successfully!',
    itemUpdatedSuccess: 'Item updated successfully!',
    itemDeletedSuccess: 'Item deleted',
    failedToAddItem: 'Failed to add item',
    failedToUpdateItem: 'Failed to update item',
    failedToDeleteItem: 'Failed to delete item',
    enterBothAirports: 'Please enter both departure and arrival airports',
    enterBothStations: 'Please enter both departure and arrival stations',
    deleteItemConfirm: 'Are you sure you want to delete this item?',

    // Share Dialog
    shareTripTitle: 'Share Trip',
    shareTripDesc: 'Share this trip with friends or family members',
    emailAddress: 'Email Address',
    emailPlaceholder: 'friend@example.com',
    tripSharedSuccess: 'Trip shared successfully!',
    failedToShareTrip: 'Failed to share trip',
    enterEmailAddress: 'Please enter an email address',

    // Gmail Connection Dialog
    gmailIntegrationTitle: 'Gmail Integration',
    gmailIntegrationDesc: 'Connect Gmail to automatically import booking confirmations',
    gmailConnected: 'Gmail Connected',
    notConnected: 'Not Connected',
    accountLabel: 'Account',
    features: 'Features',
    featureAutoCheck: 'Automatic email checking every 5 minutes',
    featureExtract: 'Extracts flights, hotels, restaurants, and more',
    featureAIPowered: 'AI-powered booking detail extraction',
    featureMarkRead: 'Marks processed emails as read',
    connectGmail: 'Connect Gmail',
    disconnectGmail: 'Disconnect Gmail',
    checkEmailsNow: 'Check Emails Now',
    checking: 'Checking...',
    disconnectGmailConfirm: 'Are you sure you want to disconnect Gmail? You will no longer receive automatic email imports.',
    gmailDisconnectedSuccess: 'Gmail disconnected successfully',
    gmailConnectedSuccessfully: 'Gmail connected successfully!',
    gmailConnectionFailed: 'Failed to connect Gmail. Please try again.',
    failedToDisconnectGmail: 'Failed to disconnect Gmail',
    failedToCheckGmailStatus: 'Failed to check Gmail connection status',
    foundBookingEmails: 'Found {count} booking email(s)!',
    noNewBookingEmails: 'No new booking emails found',
    failedToFetchEmails: 'Failed to fetch emails',
    gmailSecurityInfo: 'Your Gmail credentials are securely stored and never shared',

    // Pass Portfolio Dialog
    passPortfolioTitle: 'Pass Portfolio',
    offlineAvailable: 'Offline Available',
    passPortfolioDesc: 'Store and manage all trip-related documents: tickets, confirmations, and reservations',
    clickToUploadDocs: 'Click to upload documents',
    uploading: 'Uploading...',
    fileTypes: 'PDF, PNG, JPG (Max 5MB per file)',
    noDocumentsYet: 'No documents yet',
    uploadDocsOffline: 'Upload your travel documents to access them anytime, even offline!',
    documentsAvailableOffline: '{count} document(s) available offline',
    documentDeleted: 'Document deleted',
    documentDownloaded: 'Document downloaded',
    failedToDeleteDoc: 'Failed to delete document',
    failedToDownloadDoc: 'Failed to download document',
    fileTooLarge: '{name} is too large. Maximum size is 5MB.',
    uploadedSuccess: '{name} uploaded successfully',
    failedToUpload: 'Failed to upload {name}',

    // Pass Portfolio Tabs
    boardingPasses: 'Boarding Passes',
    eventTickets: 'Event Tickets',
    passportsVisas: 'Passports & IDs',
    otherDocs: 'Other Documents',
    noBoardingPasses: 'No boarding passes yet',
    uploadBoardingDesc: 'Upload your flight boarding passes and e-tickets',
    noEventTickets: 'No event tickets yet',
    uploadTicketsDesc: 'Upload tickets for tours, museums, concerts, and events',
    noPassports: 'No passport documents yet',
    uploadPassportsDesc: 'Store passport copies, visas, and ID documents for all travelers',
    noOtherDocs: 'No other documents yet',
    uploadOtherDesc: 'Store hotel confirmations, insurance, and other travel documents',
    dropFilesHere: 'Drop files here or click to upload',
    fileTypesExtended: 'PDF, PNG, JPG up to 10MB • Files auto-categorize by name',
    portfolioSummary: 'Portfolio Summary',

    // Email Parse Dialog
    aiEmailParseTitle: 'AI Email Parse',
    aiEmailParseDesc: 'Paste your booking confirmation email and let AI extract all the details automatically',
    pasteEmailText: 'Paste Email Text',
    pasteEmailPlaceholder: 'Paste your booking confirmation email here...',
    uploadEmailFile: 'Upload Email File (.txt, .eml)',
    clickToUploadFile: 'Click to upload file',
    supportedFileTypes: 'TXT, EML, HTML files supported',
    extractBookings: 'Extract Bookings',
    parsingWithAI: 'Parsing with AI...',
    foundBookings: 'Found {count} booking(s)',
    reviewAndAdd: 'Review and add them to your itinerary',
    addToItinerary: 'Add to Itinerary',
    addAllToItinerary: 'Add All to Itinerary',
    addingAll: 'Adding all...',
    skip: 'Skip',
    parseAnotherEmail: 'Parse Another Email',
    pleaseEnterEmail: 'Please paste email text',
    fileLoadedSuccess: 'File loaded successfully',
    failedToReadFile: 'Failed to read file',
    failedToParseEmail: 'Failed to parse email',
    noBookingsFound: 'No booking confirmations found in the email',
    emailParsedSuccess: 'Email parsed and added to itinerary!',
    addedToItinerary: '{title} added to itinerary',
    failedToAddBooking: 'Failed to add booking',

    // Destination Info Window
    loadingWeather: 'Loading weather forecast...',
    noDatesSet: 'No dates set',
    saveDatesGetWeather: 'Save Dates & Get Weather',
    selectBothDates: 'Please select both start and end dates',
    datesSavedWeatherLoaded: 'Dates saved & weather loaded!',
    failedToFetchWeather: 'Failed to fetch weather',
    unableToGetCoordinates: 'Unable to get location coordinates for weather',
    photos: 'Photos',
    photosCount: '{count} photos',
    scrollForMore: 'Scroll for more',
    clickToViewFullSize: 'Click to view full size',
    yourPlans: 'My Plans',
    topRestaurants: 'Top Restaurants',
    topAttractions: 'Top Attractions',

    // Trip Detail Misc
    homebase: 'Homebase',
    otherLocations: 'Other Locations',
    loadedTripOffline: 'Loaded trip from offline storage',
    loadedItemsOffline: 'Loaded {count} itinerary items from offline storage',
    loadedDocsOffline: 'Loaded documents from offline storage',
    failedToLoadTrip: 'Failed to load trip',
    failedToLoadItinerary: 'Failed to load itinerary',
    failedToSaveOrder: 'Failed to save new order',
    failedToLoadPlaceDetails: 'Failed to load place details',
    openingAddItemDialog: 'Opening Add Item dialog',
    restaurant: 'Restaurant',
    attraction: 'Attraction',

    // Item Type Names
    itemTypeHotel: 'Hotel',
    itemTypeRestaurant: 'Restaurant',
    itemTypeActivity: 'Activity',
    itemTypeTrain: 'Train',
    itemTypeOther: 'Itinerary Item',

    // Mobile Header Actions
    share: 'Share',
    docs: 'Docs',
    gmail: 'Gmail',
    apps: 'Apps',
    appServices: 'App Services',
    appServicesDesc: 'Manage your connected app services',
    appIntegrations: 'App Integrations',
    appIntegrationsDesc: 'Manage your connected app integrations',
    aiParse: 'AI Parse',

    // Google Services Dialog
    googleServices: 'Google Services',
    googleServicesDesc: 'Manage your connected Google services',
    googleCalendar: 'Google Calendar',
    googleWallet: 'Google Wallet',
    gmailServiceDesc: 'Auto-import booking confirmations',
    calendarServiceDesc: 'Export trips to your calendar',
    walletServiceDesc: 'Store boarding passes & tickets',
    serviceDisconnected: 'Google services disconnected',
    failedToToggleService: 'Failed to update service connection',
    failedToLoadServices: 'Failed to load services status',
    googleServicesNote: 'Gmail and Calendar share the same connection. Disconnecting one will disconnect both.',
    manageInGoogle: 'Manage permissions in Google Account',

    // Map Markers & Locations
    clickedLocation: 'Clicked Location',
    searchResult: 'Search Result',
    unableToLocateDestination: 'Unable to locate destination on map',
    unableToLocateHomebase: 'Unable to locate homebase on map',
    destinationCity: 'Destination City',
    recentReviews: 'Recent Reviews',
    seeAllReviewsOnGoogle: 'See all reviews on Google Maps',
    destinationsReordered: 'Destinations reordered!',
    failedToReorderDestinations: 'Failed to reorder destinations',

    // Map Actions
    addMarker: 'Add Marker',
    viewOnGoogleMaps: 'View on Google Maps',
    getDirections: 'Get Directions',
    addToItineraryShort: 'Add to Itinerary',

    // Weather
    weatherForecast: 'Weather Forecast',
    high: 'High',
    low: 'Low',
    precipitation: 'Precipitation',

    // Trip Summary
    tripSummary: 'Trip Summary',
    aiTripSummaryTitle: 'AI Trip Summary',
    addItemsForSummary: 'Add some items to your itinerary to see an AI-generated trip summary',
    generatingSummary: 'Generating your trip summary...',
    tryAgainButton: 'Try Again',
    failedToGenerateSummary: 'Failed to generate trip summary',
    duration: 'Duration',
    days: 'days',
    day: 'day',
    totalBudget: 'Total Budget',
    remaining: 'remaining',
    overBudget: 'over budget',

    // Place Details
    openNow: 'Open now',
    closedNow: 'Closed',
    viewHours: 'View hours',

    // General UI
    loading: 'Loading...',
    error: 'Error',
    retry: 'Retry',
    noResults: 'No results found',
    searchPlaceholder: 'Search...',

    // Button Titles & Tooltips
    backToDashboard: 'Back to Dashboard',
    clickToEditDates: 'Click to edit trip dates',
    clickToAddDates: 'Click to add trip dates',
    clickToAddBudget: 'Click to add budget',
    clickToSetBudget: 'Click to set a budget',
    clickToEditBudget: 'Click to edit budget',
    clickToChangeDates: 'Click to change dates',
    clickToSetDates: 'Click to set dates',
    setDates: 'Set dates',
    availableOffline: 'Available offline',
    offline: 'Offline',
    addDates: 'Add dates',
    addBudget: 'Add budget',
    homebaseTooltip: 'Homebase: {{location}} (click to view on map)',
    searchPlacesActivities: 'Search for places, restaurants, activities...',
    tripTitlePlaceholder: 'European Adventure 2024',
    itemTitlePlaceholder: 'e.g., Eiffel Tower Visit',
    costPlaceholder: 'e.g., 150',
    confirmationPlaceholder: 'e.g., ABC123',
    budgetPlaceholder: '5000',
    datePlaceholder: 'YYYY-MM-DD',
    viewTrip: 'View trip',

    // AI Assist
    aiAssist: 'AI Assist',
    aiAssistDesc: 'Your AI-powered trip planning assistant',
    tripSuggestions: 'Trip Suggestions',
    tripSuggestionsDesc: 'Get AI-powered recommendations for activities, restaurants, and attractions',
    itineraryOptimization: 'Itinerary Optimization',
    itineraryOptimizationDesc: 'Optimize your schedule for better time management and logistics',
    budgetRecommendations: 'Budget Recommendations',
    budgetRecommendationsDesc: 'Get cost-saving tips and budget-friendly alternatives',
    packingList: 'Packing List',
    packingListDesc: 'Generate a comprehensive packing list for your trip',
    tripChat: 'Ask Anything',
    tripChatDesc: 'Chat with AI about your trip - ask any question!',
    askAboutTrip: 'Ask anything about your trip...',
    thinking: 'Thinking...',
    askAI: 'Ask AI',
    aiGenerating: 'AI is generating your response...',
    followUpQuestion: 'Ask a follow-up question...',
    askFollowUp: 'Ask Follow-up',

    // Flight/Train Badges
    departure: 'Departure',
    arrival: 'Arrival',
    returnTo: 'Return to',

    // InfoWindow Marker Types
    departureFlight: 'Departure Flight',
    arrivalFlight: 'Arrival Flight',
    departureTrain: 'Departure Train',
    arrivalTrain: 'Arrival Train',
    embarkationPort: 'Embarkation Port',
    disembarkationPort: 'Disembarkation Port',
    searchResultMarker: 'Search Result',
    clickedLocationMarker: 'Clicked Location',
    accommodation: 'Accommodation',

    // Marker Clustering
    tapToExpand: 'Tap to expand',
    hoverToExpand: 'Hover to expand',
    departuresCount: '{count} departures',
    arrivalsCount: '{count} arrivals',

    // Document Actions (PassPortfolioDialog)
    preview: 'Preview',
    download: 'Download',

    // Itinerary Item Labels
    aiParsed: 'AI Parsed',

    // AI Assist Toast Messages
    failedToGenerateAI: 'Failed to generate AI response',
    failedToConnectAI: 'Failed to connect to AI service',
    pleaseEnterMessage: 'Please enter a message',
    copiedToClipboard: 'Copied to clipboard!',

    // Dialog Accessibility Labels

    // Budget Categories

    // Network Status
    youAreCurrentlyOffline: 'You are currently offline',
    changesWillBeSaved: 'Changes will be saved when you reconnect',
    backOnline: 'Back online',
    connected: 'Connected',

    // Error Boundary
    somethingWentWrong: 'Something went wrong',
    oopsSomethingWentWrong: 'Oops! Something went wrong',
    unexpectedErrorMessage: "We encountered an unexpected error. Don't worry, your data is safe.",
    goHome: 'Go Home',

    // Itinerary
    noItineraryItemsYet: 'No itinerary items yet',
    addYourFirstItem: 'Add Your First Item',

    // Trip Info
    noDestinationsAdded: 'No destinations added',
    thisIsOneOfYourTripDestinations: 'This is one of your trip destinations',

    // Pagination
    previous: 'Previous',
    next: 'Next',
    morePages: 'More pages',
    goToPreviousPage: 'Go to previous page',
    goToNextPage: 'Go to next page',

    // Carousel
    previousSlide: 'Previous slide',
    nextSlide: 'Next slide',

    // Keyboard Shortcuts
    ctrl: 'Ctrl',
    enter: 'Enter',

    // Network/Status
    retrying: 'Retrying...',
    online: 'Online',

    // Empty States
    noItineraryItemsYetParseEmail: 'No itinerary items yet. Add your first item or parse an email!',

    // Common Actions

    // Error Messages
    oops: 'Oops!',
    tryAgain: 'Try Again',
    networkError: 'Unable to connect. Please check your internet connection.',
    serverError: 'Server is temporarily unavailable. Please try again later.',
    notFound: 'The requested resource was not found.',
    unauthorized: 'Please log in to continue.',
    forbidden: 'You do not have permission to access this resource.',

    // Toast Messages - Errors
    errorRevokingSession: 'Error revoking session',

    // Toast Messages - Success

    // Toast Messages - Info
    loadedFromOfflineStorage: 'Loaded from offline storage',
    loadedTripsOffline: 'Loaded {count} trips from offline storage',

    // Form Validation
    pleasePasteEmailText: 'Please paste email text',
    pleaseFillRequiredFields: 'Please fill in required fields (Title and Starting Location)',

    // Placeholders
    enterDisplayName: 'Enter display name',
    typeDelete: 'DELETE',

    // Clipboard & Actions

    // Gmail/Email Parsing
    noTravelBookingsFound: 'No travel bookings found in this email',
    failedToParseEmailManual: 'Failed to parse email. Please try the manual Import Booking option.',
    addedBookingsToItinerary: 'Added {count} booking(s) to itinerary',
    failedToAddBookings: 'Failed to add {count} booking(s)',

    // Share Link Feature
    sharedTrip: 'Shared Trip',
    tripNotFound: 'Trip Not Found',
    poweredBy: 'Powered by',
    planYourOwnTrip: 'Plan your own trip',
    unscheduled: 'Unscheduled',
    shareViaLink: 'Share via Link',
    generateShareLink: 'Generate Share Link',
    shareLinkGenerated: 'Share link generated!',
    copyShareLink: 'Copy Share Link',
    disableSharing: 'Disable Sharing',
    sharingDisabled: 'Sharing has been disabled',
    shareThisTrip: 'Share this trip',
    anyoneWithLink: 'Anyone with the link can view this trip (read-only)',
    shareLinkActive: 'Share link is active',
    noShareLinkYet: 'No share link generated yet',
  },
  
  es: {
    // Navigation
    back: 'Atrás',
    settings: 'Configuración',
    profileMenu: 'Menú de perfil',
    logout: 'Cerrar sesión',
    switchAccount: 'Cambiar de cuenta',

    // Landing Page
    appName: 'iGo',
    signInWithGoogle: 'Iniciar sesión con Google',
    planYourPerfect: 'Planifica Tu Perfecto',
    journey: 'Viaje',
    tagline: 'Planificación de viajes con IA, análisis automático de correos y colaboración en tiempo real',
    builtWith: 'Hecho con ❤️ en Emergent',

    // Features
    smartEmailParsing: 'Análisis Inteligente de Correo',
    smartEmailParsingDesc: 'Extrae automáticamente reservas de confirmaciones de Gmail usando IA',
    aiItineraryBuilder: 'Creador de Itinerario con IA',
    aiItineraryBuilderDesc: 'Obtén sugerencias de viaje personalizadas según tu destino y preferencias',
    interactiveMaps: 'Mapas Interactivos',
    interactiveMapsDesc: 'Visualiza todo tu viaje en Google Maps con planificación de rutas',
    timelineView: 'Vista de Cronología',
    timelineViewDesc: 'Organiza hoteles, vuelos, actividades y restaurantes en una hermosa línea de tiempo',
    shareCollaborate: 'Compartir y Colaborar',
    shareCollaborateDesc: 'Comparte viajes con compañeros y planifica juntos en tiempo real',
    budgetTracking: 'Seguimiento de Presupuesto',
    budgetTrackingDesc: 'Mantén un registro de gastos y mantente dentro de tu presupuesto de viaje',

    // Dashboard
    welcomeBack: 'Bienvenido de nuevo',
    readyToPlanner: '¿Listo para planear tu próxima aventura?',
    createNewTrip: 'Crear Nuevo Viaje',
    noTripsYet: 'Aún no hay viajes',
    createFirstTrip: '¡Crea tu primer viaje para comenzar!',
    loadingTrips: 'Cargando viajes...',

    // Trip Detail
    shareTrip: 'Compartir Viaje',
    editTrip: 'Editar Viaje',
    aiEmailParse: 'Análisis de Correo IA',
    gmailIntegration: 'Integración Gmail',
    exportToCalendar: 'Exportar a Calendario',
    exportCalendar: 'Calendario',
    calendarExportSuccess: 'Exportado a Google Calendar exitosamente',
    calendarExportFailed: 'Error al exportar al calendario',
    noItemsToExport: 'No hay elementos de itinerario para exportar',
    connectGmailFirst: 'Conecta Gmail desde el Panel para habilitar exportación al calendario',
    passPortfolio: 'Portafolio de Pases',
    budget: 'Presupuesto',
    used: 'usado',
    addItem: 'Agregar Elemento',
    parseEmail: 'Importar Reserva',
    emailsFound: 'Correos Encontrados',
    reviewAndAddToItinerary: 'Revisar y Agregar al Itinerario',
    parseAndAdd: 'Analizar y Agregar al Itinerario',
    noEmailsFound: 'No se encontraron correos de reserva',
    bookingsFound: 'Reservas Encontradas',
    selectBookingsToAdd: 'Selecciona las reservas para agregar a tu itinerario',
    adding: 'Agregando...',
    noItemsYet: '¡Aún no hay elementos de itinerario. Agrega tu primer elemento o analiza un correo!',

    // Budget
    budgetOverview: 'Resumen del Presupuesto',
    setBudgetToTrack: 'Establece un presupuesto para ver el seguimiento de gastos',
    lodging: 'Alojamiento',
    travel: 'Viajes',
    dining: 'Comidas',
    activities: 'Actividades',

    // Settings
    settingsTitle: 'Configuración',
    account: 'Cuenta',
    accountDesc: 'Administra la configuración y preferencias de tu cuenta.',
    notifications: 'Notificaciones',
    notificationsDesc: 'Controla cómo recibes notificaciones.',
    privacySecurity: 'Privacidad y Seguridad',
    privacySecurityDesc: 'Administra tus datos y configuración de seguridad.',
    privacyPolicy: 'Política de Privacidad',
    termsOfService: 'Términos de Servicio',
    appearance: 'Apariencia',
    appearanceDesc: 'Personaliza el aspecto de la aplicación.',
    language: 'Idioma',
    languageDesc: 'Elige tu idioma preferido',
    theme: 'Tema',
    light: 'Claro',
    lightThemeDesc: 'Usar tema claro',
    dark: 'Oscuro',
    darkThemeDesc: 'Usar tema oscuro',
    system: 'Sistema',
    systemThemeDesc: 'Coincidir con el tema del dispositivo',
    comingSoon: 'Próximamente',
    close: 'Cerrar',

    // Account Settings
    profileInfo: 'Información del Perfil',
    displayName: 'Nombre para Mostrar',
    displayNameDesc: 'Este nombre se mostrará a otros cuando compartas viajes',
    email: 'Correo Electrónico',
    emailDesc: 'Tu correo de inicio de sesión (gestionado por Google)',
    memberSince: 'Miembro desde',
    preferences: 'Preferencias',
    defaultCurrency: 'Moneda Predeterminada',
    distanceUnit: 'Unidad de Distancia',
    kilometers: 'Kilómetros',
    miles: 'Millas',
    temperatureUnit: 'Unidad de Temperatura',
    celsius: 'Celsius',
    fahrenheit: 'Fahrenheit',
    dateFormat: 'Formato de Fecha',
    timeFormat: 'Formato de Hora',
    hour12: '12 horas',
    hour24: '24 horas',
    saveChanges: 'Guardar Cambios',
    saving: 'Guardando...',
    changesSaved: 'Cambios guardados exitosamente',
    errorSaving: 'Error al guardar cambios',

    // Notification Settings
    emailNotifications: 'Notificaciones por Correo',
    emailNotificationsDesc: 'Recibe actualizaciones sobre tus viajes por correo',
    tripReminders: 'Recordatorios de Viaje',
    tripRemindersDesc: 'Recibe recordatorios antes de que comiencen tus viajes',
    shareNotifications: 'Notificaciones de Compartir',
    shareNotificationsDesc: 'Recibe notificaciones cuando alguien comparta un viaje contigo',
    marketingEmails: 'Correos de Marketing',
    marketingEmailsDesc: 'Recibe noticias y ofertas promocionales',

    // Privacy & Security
    dataExport: 'Exportar Datos',
    dataExportDesc: 'Descarga todos tus datos incluyendo viajes, itinerarios y preferencias',
    exportData: 'Exportar Mis Datos',
    exporting: 'Exportando...',
    exportSuccess: 'Datos exportados exitosamente',
    exportError: 'Error al exportar datos',
    activeSessions: 'Sesiones Activas',
    activeSessionsDesc: 'Administra los dispositivos y sesiones conectados a tu cuenta',
    currentSession: 'Sesión Actual',
    revokeSession: 'Revocar',
    revokeSessionConfirm: '¿Estás seguro de que quieres cerrar esta sesión?',
    sessionRevoked: 'Sesión revocada exitosamente',
    noOtherSessions: 'No hay otras sesiones activas',
    dangerZone: 'Zona de Peligro',
    deleteAccount: 'Eliminar Cuenta',
    deleteAccountDesc: 'Elimina permanentemente tu cuenta y todos los datos asociados. Esta acción no se puede deshacer.',
    deleteAccountConfirm: '¿Estás absolutamente seguro de que quieres eliminar tu cuenta? Esto eliminará permanentemente todos tus viajes, itinerarios, documentos y preferencias. Esta acción NO se puede deshacer.',
    typeToConfirm: 'Escribe DELETE para confirmar',
    accountDeleted: 'Cuenta eliminada exitosamente',
    deleteAccountError: 'Error al eliminar cuenta',
    createdAt: 'Creada',
    expiresAt: 'Expira',

    // Common
    from: 'Desde',
    to: 'Hacia',
    date: 'Fecha',
    cost: 'Costo',
    location: 'Ubicación',
    notes: 'Notas',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    confirm: 'Confirmar',
    optional: '(opcional)',
    or: 'O',
    type: 'Tipo',
    title: 'Título',

    // Create/Edit Trip Dialog
    createNewTripTitle: 'Crear Nuevo Viaje',
    editTripTitle: 'Editar Viaje',
    fillDetails: 'Completa los detalles a continuación para crear un nuevo viaje',
    updateDetails: 'Actualiza los detalles de tu viaje a continuación',
    tripTitle: 'Título del Viaje',
    tripTitleRequired: 'Título del Viaje *',
    startingFrom: 'Partiendo Desde',
    startingFromRequired: 'Partiendo Desde *',
    destinations: 'Destinos',
    destinationsOptional: 'Destinos (Opcional)',
    searchLocation: 'Buscar ubicación (ej., Nueva York, EE.UU.)',
    searchDestination: 'Buscar destino (ej., París, Francia)',
    startDate: 'Fecha de Inicio',
    endDate: 'Fecha de Fin',
    createTrip: 'Crear Viaje',
    updateTrip: 'Actualizar Viaje',
    deleteConfirm: '¿Estás seguro de que quieres eliminar este viaje? Esta acción no se puede deshacer.',
    tripCreatedSuccess: '¡Viaje creado exitosamente!',
    tripUpdatedSuccess: '¡Viaje actualizado exitosamente!',
    tripDeletedSuccess: '¡Viaje eliminado exitosamente!',
    requiredFields: 'Por favor completa los campos requeridos (Título y Ubicación de Inicio)',
    sessionExpired: 'Sesión expirada. Por favor actualiza la página.',
    failedToCreateTrip: 'Error al crear el viaje',
    failedToUpdateTrip: 'Error al actualizar el viaje',
    failedToDeleteTrip: 'Error al eliminar el viaje',
    failedToLoadTrips: 'Error al cargar los viajes',
    loadedFromOffline: 'Viajes cargados desde almacenamiento sin conexión',
    sharedWith: 'Compartido con',
    person: 'persona',
    people: 'personas',
    currency: 'Moneda',

    // Add/Edit Itinerary Dialog
    addItineraryItem: 'Agregar Elemento al Itinerario',
    addItineraryDesc: 'Agrega una nueva actividad, vuelo, hotel u otro elemento a tu itinerario',
    editItineraryItem: 'Editar Elemento del Itinerario',
    editItineraryDesc: 'Actualiza los detalles de este elemento del itinerario',
    typeHotel: 'Hotel',
    typeFlight: 'Vuelo',
    typeTrain: 'Tren',
    typeVacationRental: 'Alquiler Vacacional',
    typeCruise: 'Crucero',
    typeActivity: 'Actividad',
    typeRestaurant: 'Restaurante',
    typeOther: 'Otro',
    embarkAt: 'Embarcar En',
    debarkAt: 'Desembarcar En',
    pleaseEnterBothPorts: 'Por favor ingrese ambos puertos de embarque y desembarque',
    embark: 'Embarcar',
    debark: 'Desembarcar',
    disembark: 'Desembarcar',
    fullRoute: 'Ruta Completa',
    confirmation: 'Confirmación',
    portDeparture: 'Salida',
    portArrival: 'Puerto de Escala',
    searchPortExample: 'Buscar puerto (ej., Puerto de Miami)',
    searchPortArrival: 'Buscar puerto (ej., Puerto Cañaveral)',
    typeToSearchPorts: 'Escribe para buscar puertos de crucero',
    portsOfCall: 'Puertos de Escala',
    portsOfCallDesc: 'Agregar paradas intermedias en tu crucero',
    addPort: 'Agregar Puerto',
    noPortsAdded: 'Aún no se han agregado puertos',
    portName: 'Nombre del Puerto',
    searchPortPlaceholder: 'ej., Cozumel, México',
    arrivalDate: 'Llegada',
    departureDate: 'Salida',
    portOfCall: 'Puerto de Escala',
    cruiseLeg: 'Tramo',
    of: 'de',
    departingFrom: 'Saliendo Desde',
    arrivingAt: 'Llegando A',
    searchAirport: 'Buscar aeropuerto',
    searchAirportExample: 'Buscar aeropuerto (ej., Los Ángeles o LAX)',
    searchAirportArrival: 'Buscar aeropuerto (ej., Nueva York o JFK)',
    searchStation: 'Buscar estación',
    searchStationExample: 'Buscar estación (ej., Paris Gare du Nord)',
    searchStationArrival: 'Buscar estación (ej., London St Pancras)',
    searchLocationExample: 'Buscar ubicación (ej., Torre Eiffel, París)',
    typeToSearchAirports: 'Escribe para buscar aeropuertos',
    typeToSearchStations: 'Escribe para buscar estaciones de tren',
    searchOrEnterLocation: 'Buscar o ingresar ubicación',
    startDateTime: 'Fecha/Hora de Inicio',
    endDateTime: 'Fecha/Hora de Fin',
    confirmationNumber: 'Número de Confirmación',
    additionalNotes: 'Agregar notas adicionales...',
    additionalDetails: 'Detalles adicionales...',
    saveItem: 'Guardar Elemento',
    updateItem: 'Actualizar Elemento',
    itemAddedSuccess: '¡Elemento agregado exitosamente!',
    itemUpdatedSuccess: '¡Elemento actualizado exitosamente!',
    itemDeletedSuccess: 'Elemento eliminado',
    failedToAddItem: 'Error al agregar elemento',
    failedToUpdateItem: 'Error al actualizar elemento',
    failedToDeleteItem: 'Error al eliminar elemento',
    enterBothAirports: 'Por favor ingresa ambos aeropuertos de salida y llegada',
    enterBothStations: 'Por favor ingresa ambas estaciones de salida y llegada',
    deleteItemConfirm: '¿Estás seguro de que quieres eliminar este elemento?',

    // Share Dialog
    shareTripTitle: 'Compartir Viaje',
    shareTripDesc: 'Comparte este viaje con amigos o familiares',
    emailAddress: 'Correo Electrónico',
    emailPlaceholder: 'amigo@ejemplo.com',
    tripSharedSuccess: '¡Viaje compartido exitosamente!',
    failedToShareTrip: 'Error al compartir el viaje',
    enterEmailAddress: 'Por favor ingresa una dirección de correo',

    // Gmail Connection Dialog
    gmailIntegrationTitle: 'Integración Gmail',
    gmailIntegrationDesc: 'Conecta Gmail para importar automáticamente confirmaciones de reservas',
    gmailConnected: 'Gmail Conectado',
    notConnected: 'No Conectado',
    accountLabel: 'Cuenta',
    features: 'Características',
    featureAutoCheck: 'Verificación automática de correos cada 5 minutos',
    featureExtract: 'Extrae vuelos, hoteles, restaurantes y más',
    featureAIPowered: 'Extracción de detalles de reserva con IA',
    featureMarkRead: 'Marca los correos procesados como leídos',
    connectGmail: 'Conectar Gmail',
    disconnectGmail: 'Desconectar Gmail',
    checkEmailsNow: 'Verificar Correos Ahora',
    checking: 'Verificando...',
    disconnectGmailConfirm: '¿Estás seguro de que quieres desconectar Gmail? Ya no recibirás importaciones automáticas de correos.',
    gmailDisconnectedSuccess: 'Gmail desconectado exitosamente',
    gmailConnectedSuccessfully: '¡Gmail conectado exitosamente!',
    gmailConnectionFailed: 'Error al conectar Gmail. Por favor, inténtalo de nuevo.',
    failedToDisconnectGmail: 'Error al desconectar Gmail',
    failedToCheckGmailStatus: 'Error al verificar el estado de conexión de Gmail',
    foundBookingEmails: '¡Se encontraron {count} correo(s) de reserva!',
    noNewBookingEmails: 'No se encontraron nuevos correos de reserva',
    failedToFetchEmails: 'Error al obtener correos',
    gmailSecurityInfo: 'Tus credenciales de Gmail se almacenan de forma segura y nunca se comparten',

    // Pass Portfolio Dialog
    passPortfolioTitle: 'Portafolio de Pases',
    offlineAvailable: 'Disponible Sin Conexión',
    passPortfolioDesc: 'Almacena y gestiona todos los documentos de tu viaje: boletos, confirmaciones y reservas',
    clickToUploadDocs: 'Clic para subir documentos',
    uploading: 'Subiendo...',
    fileTypes: 'PDF, PNG, JPG (Máx 5MB por archivo)',
    noDocumentsYet: 'Aún no hay documentos',
    uploadDocsOffline: '¡Sube tus documentos de viaje para acceder a ellos en cualquier momento, incluso sin conexión!',
    documentsAvailableOffline: '{count} documento(s) disponible(s) sin conexión',
    documentDeleted: 'Documento eliminado',
    documentDownloaded: 'Documento descargado',
    failedToDeleteDoc: 'Error al eliminar documento',
    failedToDownloadDoc: 'Error al descargar documento',
    fileTooLarge: '{name} es demasiado grande. El tamaño máximo es 5MB.',
    uploadedSuccess: '{name} subido exitosamente',
    failedToUpload: 'Error al subir {name}',

    // Pass Portfolio Tabs
    boardingPasses: 'Tarjetas de Embarque',
    eventTickets: 'Boletos de Eventos',
    passportsVisas: 'Pasaportes e IDs',
    otherDocs: 'Otros Documentos',
    noBoardingPasses: 'Aún no hay tarjetas de embarque',
    uploadBoardingDesc: 'Sube tus tarjetas de embarque y boletos electrónicos',
    noEventTickets: 'Aún no hay boletos de eventos',
    uploadTicketsDesc: 'Sube boletos de tours, museos, conciertos y eventos',
    noPassports: 'Aún no hay documentos de pasaporte',
    uploadPassportsDesc: 'Guarda copias de pasaportes, visas y documentos de identidad para todos los viajeros',
    noOtherDocs: 'Aún no hay otros documentos',
    uploadOtherDesc: 'Guarda confirmaciones de hotel, seguros y otros documentos de viaje',
    dropFilesHere: 'Arrastra archivos aquí o haz clic para subir',
    fileTypesExtended: 'PDF, PNG, JPG hasta 10MB • Los archivos se categorizan automáticamente por nombre',
    portfolioSummary: 'Resumen del Portafolio',

    // Email Parse Dialog
    aiEmailParseTitle: 'Análisis de Correo con IA',
    aiEmailParseDesc: 'Pega tu correo de confirmación de reserva y deja que la IA extraiga todos los detalles automáticamente',
    pasteEmailText: 'Pegar Texto del Correo',
    pasteEmailPlaceholder: 'Pega tu correo de confirmación de reserva aquí...',
    uploadEmailFile: 'Subir Archivo de Correo (.txt, .eml)',
    clickToUploadFile: 'Clic para subir archivo',
    supportedFileTypes: 'Archivos TXT, EML, HTML soportados',
    extractBookings: 'Extraer Reservas',
    parsingWithAI: 'Analizando con IA...',
    foundBookings: 'Se encontraron {count} reserva(s)',
    reviewAndAdd: 'Revisa y agrégalas a tu itinerario',
    addToItinerary: 'Agregar al Itinerario',
    addAllToItinerary: 'Agregar Todo al Itinerario',
    addingAll: 'Agregando todo...',
    skip: 'Omitir',
    parseAnotherEmail: 'Analizar Otro Correo',
    pleaseEnterEmail: 'Por favor pega el texto del correo',
    fileLoadedSuccess: 'Archivo cargado exitosamente',
    failedToReadFile: 'Error al leer archivo',
    failedToParseEmail: 'Error al analizar correo',
    noBookingsFound: 'No se encontraron confirmaciones de reserva en el correo',
    emailParsedSuccess: '¡Correo analizado y agregado al itinerario!',
    addedToItinerary: '{title} agregado al itinerario',
    failedToAddBooking: 'Error al agregar reserva',

    // Destination Info Window
    loadingWeather: 'Cargando pronóstico del clima...',
    noDatesSet: 'Sin fechas establecidas',
    saveDatesGetWeather: 'Guardar Fechas y Obtener Clima',
    selectBothDates: 'Por favor selecciona ambas fechas de inicio y fin',
    datesSavedWeatherLoaded: '¡Fechas guardadas y clima cargado!',
    failedToFetchWeather: 'Error al obtener el clima',
    unableToGetCoordinates: 'No se pueden obtener las coordenadas de ubicación para el clima',
    photos: 'Fotos',
    photosCount: '{count} fotos',
    scrollForMore: 'Desplaza para ver más',
    clickToViewFullSize: 'Clic para ver tamaño completo',
    yourPlans: 'Mis Planes',
    topRestaurants: 'Mejores Restaurantes',
    topAttractions: 'Mejores Atracciones',

    // Trip Detail Misc
    homebase: 'Base de Inicio',
    destinationCity: 'Ciudad de Destino',
    recentReviews: 'Reseñas Recientes',
    seeAllReviewsOnGoogle: 'Ver todas las reseñas en Google Maps',
    otherLocations: 'Otras Ubicaciones',
    loadedTripOffline: 'Viaje cargado desde almacenamiento sin conexión',
    loadedItemsOffline: '{count} elementos de itinerario cargados desde almacenamiento sin conexión',
    loadedDocsOffline: 'Documentos cargados desde almacenamiento sin conexión',
    failedToLoadTrip: 'Error al cargar el viaje',
    failedToLoadItinerary: 'Error al cargar el itinerario',
    failedToSaveOrder: 'Error al guardar el nuevo orden',
    failedToLoadPlaceDetails: 'Error al cargar detalles del lugar',
    openingAddItemDialog: 'Abriendo diálogo de agregar elemento',
    restaurant: 'Restaurante',
    attraction: 'Atracción',

    // Item Type Names
    itemTypeHotel: 'Hotel',
    itemTypeRestaurant: 'Restaurante',
    itemTypeActivity: 'Actividad',
    itemTypeTrain: 'Tren',
    itemTypeOther: 'Elemento del Itinerario',

    // Mobile Header Actions
    share: 'Compartir',
    docs: 'Docs',
    gmail: 'Gmail',
    apps: 'Apps',
    appServices: 'Servicios de Apps',
    appServicesDesc: 'Administra tus servicios de apps conectados',
    appIntegrations: 'Integraciones de Apps',
    appIntegrationsDesc: 'Administra tus integraciones de apps conectadas',
    aiParse: 'IA Parsear',

    // Google Services Dialog
    googleServices: 'Servicios de Google',
    googleServicesDesc: 'Administra tus servicios de Google conectados',
    googleCalendar: 'Google Calendar',
    googleWallet: 'Google Wallet',
    gmailServiceDesc: 'Importar automáticamente confirmaciones de reservas',
    calendarServiceDesc: 'Exportar viajes a tu calendario',
    walletServiceDesc: 'Guardar tarjetas de embarque y tickets',
    serviceDisconnected: 'Servicios de Google desconectados',
    failedToToggleService: 'Error al actualizar conexión del servicio',
    failedToLoadServices: 'Error al cargar estado de servicios',
    googleServicesNote: 'Gmail y Calendar comparten la misma conexión. Desconectar uno desconectará ambos.',
    manageInGoogle: 'Administrar permisos en cuenta de Google',

    // Map Markers & Locations
    clickedLocation: 'Ubicación Seleccionada',
    searchResult: 'Resultado de Búsqueda',
    unableToLocateDestination: 'No se puede ubicar el destino en el mapa',
    unableToLocateHomebase: 'No se puede ubicar la base en el mapa',
    destinationsReordered: '¡Destinos reordenados!',
    failedToReorderDestinations: 'Error al reordenar destinos',

    // Map Actions
    addMarker: 'Agregar Marcador',
    viewOnGoogleMaps: 'Ver en Google Maps',
    getDirections: 'Obtener Direcciones',
    addToItineraryShort: 'Agregar al Itinerario',

    // Weather
    weatherForecast: 'Pronóstico del Tiempo',
    high: 'Máx',
    low: 'Mín',
    precipitation: 'Precipitación',

    // Trip Summary
    tripSummary: 'Resumen del Viaje',
    aiTripSummaryTitle: 'Resumen de Viaje con IA',
    addItemsForSummary: 'Agrega elementos a tu itinerario para ver un resumen generado por IA',
    generatingSummary: 'Generando el resumen de tu viaje...',
    tryAgainButton: 'Intentar de Nuevo',
    failedToGenerateSummary: 'Error al generar el resumen del viaje',
    duration: 'Duración',
    days: 'días',
    day: 'día',
    totalBudget: 'Presupuesto Total',
    remaining: 'restante',
    overBudget: 'sobre presupuesto',

    // Place Details
    openNow: 'Abierto ahora',
    closedNow: 'Cerrado',
    viewHours: 'Ver horarios',

    // General UI
    loading: 'Cargando...',
    error: 'Error',
    retry: 'Reintentar',
    noResults: 'No se encontraron resultados',
    searchPlaceholder: 'Buscar...',

    // Button Titles & Tooltips
    backToDashboard: 'Volver al Panel',
    clickToEditDates: 'Clic para editar fechas del viaje',
    clickToAddDates: 'Clic para agregar fechas del viaje',
    clickToAddBudget: 'Clic para agregar presupuesto',
    clickToSetBudget: 'Clic para establecer presupuesto',
    clickToEditBudget: 'Clic para editar presupuesto',
    clickToChangeDates: 'Clic para cambiar fechas',
    clickToSetDates: 'Clic para establecer fechas',
    setDates: 'Establecer fechas',
    availableOffline: 'Disponible sin conexión',
    offline: 'Sin conexión',
    addDates: 'Agregar fechas',
    addBudget: 'Agregar presupuesto',
    homebaseTooltip: 'Base: {{location}} (clic para ver en el mapa)',
    searchPlacesActivities: 'Buscar lugares, restaurantes, actividades...',
    tripTitlePlaceholder: 'Aventura Europea 2024',
    itemTitlePlaceholder: 'ej., Visita a la Torre Eiffel',
    costPlaceholder: 'ej., 150',
    confirmationPlaceholder: 'ej., ABC123',
    budgetPlaceholder: '5000',
    datePlaceholder: 'AAAA-MM-DD',
    viewTrip: 'Ver viaje',

    // AI Assist
    aiAssist: 'Asistente IA',
    aiAssistDesc: 'Tu asistente de planificación de viajes con IA',
    tripSuggestions: 'Sugerencias de Viaje',
    tripSuggestionsDesc: 'Obtén recomendaciones de IA para actividades, restaurantes y atracciones',
    itineraryOptimization: 'Optimización del Itinerario',
    itineraryOptimizationDesc: 'Optimiza tu horario para mejor gestión del tiempo y logística',
    budgetRecommendations: 'Recomendaciones de Presupuesto',
    budgetRecommendationsDesc: 'Obtén consejos para ahorrar y alternativas económicas',
    packingList: 'Lista de Equipaje',
    packingListDesc: 'Genera una lista completa de equipaje para tu viaje',
    tripChat: 'Pregunta lo que Sea',
    tripChatDesc: 'Chatea con IA sobre tu viaje - ¡haz cualquier pregunta!',
    askAboutTrip: 'Pregunta lo que quieras sobre tu viaje...',
    thinking: 'Pensando...',
    askAI: 'Preguntar a IA',
    aiGenerating: 'La IA está generando tu respuesta...',
    followUpQuestion: 'Haz una pregunta de seguimiento...',
    askFollowUp: 'Pregunta de Seguimiento',

    // Flight/Train Badges
    departure: 'Salida',
    arrival: 'Llegada',
    returnTo: 'Regreso a',

    // InfoWindow Marker Types
    departureFlight: 'Vuelo de Salida',
    arrivalFlight: 'Vuelo de Llegada',
    departureTrain: 'Tren de Salida',
    arrivalTrain: 'Tren de Llegada',
    embarkationPort: 'Puerto de Embarque',
    disembarkationPort: 'Puerto de Desembarque',
    searchResultMarker: 'Resultado de Búsqueda',
    clickedLocationMarker: 'Ubicación Seleccionada',
    accommodation: 'Alojamiento',

    // Marker Clustering
    tapToExpand: 'Toca para expandir',
    hoverToExpand: 'Pasa el cursor para expandir',
    departuresCount: '{count} salidas',
    arrivalsCount: '{count} llegadas',

    // Document Actions (PassPortfolioDialog)
    preview: 'Vista previa',
    download: 'Descargar',

    // Itinerary Item Labels
    aiParsed: 'Analizado por IA',

    // AI Assist Toast Messages
    failedToGenerateAI: 'Error al generar respuesta de IA',
    failedToConnectAI: 'Error al conectar con el servicio de IA',
    pleaseEnterMessage: 'Por favor ingresa un mensaje',
    copiedToClipboard: '¡Copiado al portapapeles!',

    // Dialog Accessibility Labels

    // Budget Categories

    // Network Status
    youAreCurrentlyOffline: 'Estás sin conexión',
    changesWillBeSaved: 'Los cambios se guardarán cuando te reconectes',
    backOnline: 'Conectado de nuevo',
    connected: 'Conectado',

    // Error Boundary
    somethingWentWrong: 'Algo salió mal',
    oopsSomethingWentWrong: '¡Ups! Algo salió mal',
    unexpectedErrorMessage: 'Encontramos un error inesperado. No te preocupes, tus datos están seguros.',
    goHome: 'Ir al inicio',

    // Itinerary
    noItineraryItemsYet: 'Aún no hay elementos en el itinerario',
    addYourFirstItem: 'Agrega tu primer elemento',

    // Trip Info
    noDestinationsAdded: 'No hay destinos agregados',
    thisIsOneOfYourTripDestinations: 'Este es uno de los destinos de tu viaje',

    // Pagination
    previous: 'Anterior',
    next: 'Siguiente',
    morePages: 'Más páginas',
    goToPreviousPage: 'Ir a la página anterior',
    goToNextPage: 'Ir a la página siguiente',

    // Carousel
    previousSlide: 'Diapositiva anterior',
    nextSlide: 'Diapositiva siguiente',

    // Keyboard Shortcuts
    ctrl: 'Ctrl',
    enter: 'Enter',

    // Network/Status
    retrying: 'Reintentando...',
    online: 'En línea',

    // Empty States
    noItineraryItemsYetParseEmail: '¡Aún no hay elementos en el itinerario. Agrega tu primer elemento o analiza un correo!',

    // Common Actions

    // Error Messages
    oops: '¡Ups!',
    tryAgain: 'Intentar de nuevo',
    networkError: 'No se puede conectar. Por favor, verifica tu conexión a internet.',
    serverError: 'El servidor no está disponible temporalmente. Por favor, intenta más tarde.',
    notFound: 'El recurso solicitado no fue encontrado.',
    unauthorized: 'Por favor, inicia sesión para continuar.',
    forbidden: 'No tienes permiso para acceder a este recurso.',

    // Toast Messages - Errors
    errorRevokingSession: 'Error al revocar sesión',

    // Toast Messages - Success

    // Toast Messages - Info
    loadedFromOfflineStorage: 'Cargado desde almacenamiento sin conexión',
    loadedTripsOffline: '{count} viajes cargados desde almacenamiento sin conexión',

    // Form Validation
    pleasePasteEmailText: 'Por favor, pega el texto del correo',
    pleaseFillRequiredFields: 'Por favor, completa los campos requeridos (Título y Ubicación de inicio)',

    // Placeholders
    enterDisplayName: 'Ingresa nombre para mostrar',
    typeDelete: 'ELIMINAR',

    // Clipboard & Actions

    // Gmail/Email Parsing
    noTravelBookingsFound: 'No se encontraron reservas de viaje en este correo',
    failedToParseEmailManual: 'Error al analizar correo. Por favor, usa la opción manual de Importar Reserva.',
    addedBookingsToItinerary: 'Se agregaron {count} reserva(s) al itinerario',
    failedToAddBookings: 'Error al agregar {count} reserva(s)',

    // Share Link Feature
    sharedTrip: 'Viaje Compartido',
    tripNotFound: 'Viaje No Encontrado',
    poweredBy: 'Desarrollado por',
    planYourOwnTrip: 'Planifica tu propio viaje',
    unscheduled: 'Sin programar',
    shareViaLink: 'Compartir por Enlace',
    generateShareLink: 'Generar Enlace',
    shareLinkGenerated: '¡Enlace generado!',
    copyShareLink: 'Copiar Enlace',
    disableSharing: 'Desactivar Compartir',
    sharingDisabled: 'Se ha desactivado el compartir',
    shareThisTrip: 'Compartir este viaje',
    anyoneWithLink: 'Cualquiera con el enlace puede ver este viaje (solo lectura)',
    shareLinkActive: 'El enlace está activo',
    noShareLinkYet: 'Aún no se ha generado un enlace',
  },
  
  fr: {
    // Navigation
    back: 'Retour',
    settings: 'Paramètres',
    profileMenu: 'Menu du profil',
    logout: 'Se déconnecter',
    switchAccount: 'Changer de compte',

    // Landing Page
    appName: 'iGo',
    signInWithGoogle: 'Se connecter avec Google',
    planYourPerfect: 'Planifiez Votre Parfait',
    journey: 'Voyage',
    tagline: 'Planification de voyage par IA avec analyse automatique des e-mails et collaboration en temps réel',
    builtWith: 'Fait avec ❤️ sur Emergent',

    // Features
    smartEmailParsing: 'Analyse Intelligente des E-mails',
    smartEmailParsingDesc: 'Extrayez automatiquement les réservations des confirmations Gmail avec IA',
    aiItineraryBuilder: "Créateur d\'Itinéraire IA",
    aiItineraryBuilderDesc: 'Obtenez des suggestions de voyage personnalisées selon votre destination',
    interactiveMaps: 'Cartes Interactives',
    interactiveMapsDesc: "Visualisez tout votre voyage sur Google Maps avec planification d\'itinéraire",
    timelineView: 'Vue Chronologique',
    timelineViewDesc: 'Organisez hôtels, vols, activités et restaurants dans une belle chronologie',
    shareCollaborate: 'Partager et Collaborer',
    shareCollaborateDesc: 'Partagez des voyages avec des compagnons et planifiez ensemble en temps réel',
    budgetTracking: 'Suivi du Budget',
    budgetTrackingDesc: 'Gardez une trace des dépenses et restez dans votre budget de voyage',

    // Dashboard
    welcomeBack: 'Bon retour',
    readyToPlanner: 'Prêt à planifier votre prochaine aventure?',
    createNewTrip: 'Créer Nouveau Voyage',
    noTripsYet: 'Pas encore de voyages',
    createFirstTrip: 'Créez votre premier voyage pour commencer!',
    loadingTrips: 'Chargement des voyages...',

    // Trip Detail
    shareTrip: 'Partager Voyage',
    editTrip: 'Modifier Voyage',
    aiEmailParse: 'Analyse E-mail IA',
    gmailIntegration: 'Intégration Gmail',
    exportToCalendar: 'Exporter vers Calendrier',
    exportCalendar: 'Calendrier',
    calendarExportSuccess: 'Exporté vers Google Calendar avec succès',
    calendarExportFailed: "Échec de l\'exportation vers le calendrier",
    noItemsToExport: "Aucun élément d\'itinéraire à exporter",
    connectGmailFirst: "Connectez Gmail depuis le tableau de bord pour activer l\'exportation calendrier",
    passPortfolio: 'Portfolio de Passes',
    budget: 'Budget',
    used: 'utilisé',
    addItem: 'Ajouter Élément',
    parseEmail: 'Importer Réservation',
    emailsFound: 'E-mails Trouvés',
    reviewAndAddToItinerary: "Examiner et Ajouter à l\'Itinéraire",
    parseAndAdd: "Analyser et Ajouter à l\'Itinéraire",
    noEmailsFound: 'Aucun e-mail de réservation trouvé',
    bookingsFound: 'Réservations Trouvées',
    selectBookingsToAdd: 'Sélectionnez les réservations à ajouter à votre itinéraire',
    adding: 'Ajout en cours...',
    noItemsYet: "Pas encore d\'éléments d\'itinéraire. Ajoutez votre premier élément ou analysez un e-mail!",

    // Budget
    budgetOverview: 'Aperçu du Budget',
    setBudgetToTrack: 'Définissez un budget pour voir le suivi des dépenses',
    lodging: 'Hébergement',
    travel: 'Transport',
    dining: 'Repas',
    activities: 'Activités',

    // Settings
    settingsTitle: 'Paramètres',
    account: 'Compte',
    accountDesc: 'Gérez les paramètres et préférences de votre compte.',
    notifications: 'Notifications',
    notificationsDesc: 'Contrôlez comment vous recevez les notifications.',
    privacySecurity: 'Confidentialité et Sécurité',
    privacySecurityDesc: 'Gérez vos données et paramètres de sécurité.',
    privacyPolicy: 'Politique de Confidentialité',
    termsOfService: "Conditions d\'Utilisation",
    appearance: 'Apparence',
    appearanceDesc: "Personnalisez l\'apparence de l\'application.",
    language: 'Langue',
    languageDesc: 'Choisissez votre langue préférée',
    theme: 'Thème',
    light: 'Clair',
    lightThemeDesc: 'Utiliser le thème clair',
    dark: 'Sombre',
    darkThemeDesc: 'Utiliser le thème sombre',
    system: 'Système',
    systemThemeDesc: "Correspondre au thème de l\'appareil",
    comingSoon: 'Bientôt disponible',
    close: 'Fermer',

    // Account Settings
    profileInfo: 'Informations du Profil',
    displayName: "Nom d\'Affichage",
    displayNameDesc: 'Ce nom sera montré aux autres lorsque vous partagez des voyages',
    email: 'E-mail',
    emailDesc: 'Votre e-mail de connexion (géré par Google)',
    memberSince: 'Membre depuis',
    preferences: 'Préférences',
    defaultCurrency: 'Devise par Défaut',
    distanceUnit: 'Unité de Distance',
    kilometers: 'Kilomètres',
    miles: 'Miles',
    temperatureUnit: 'Unité de Température',
    celsius: 'Celsius',
    fahrenheit: 'Fahrenheit',
    dateFormat: 'Format de Date',
    timeFormat: "Format d\'Heure",
    hour12: '12 heures',
    hour24: '24 heures',
    saveChanges: 'Enregistrer les Modifications',
    saving: 'Enregistrement...',
    changesSaved: 'Modifications enregistrées avec succès',
    errorSaving: "Erreur lors de l\'enregistrement",

    // Notification Settings
    emailNotifications: 'Notifications par E-mail',
    emailNotificationsDesc: 'Recevoir des mises à jour sur vos voyages par e-mail',
    tripReminders: 'Rappels de Voyage',
    tripRemindersDesc: 'Recevoir des rappels avant le début de vos voyages',
    shareNotifications: 'Notifications de Partage',
    shareNotificationsDesc: "Être notifié quand quelqu\'un partage un voyage avec vous",
    marketingEmails: 'E-mails Marketing',
    marketingEmailsDesc: 'Recevoir des nouvelles et offres promotionnelles',

    // Privacy & Security
    dataExport: 'Export des Données',
    dataExportDesc: 'Téléchargez toutes vos données y compris les voyages, itinéraires et préférences',
    exportData: 'Exporter Mes Données',
    exporting: 'Exportation...',
    exportSuccess: 'Données exportées avec succès',
    exportError: "Erreur lors de l\'exportation",
    activeSessions: 'Sessions Actives',
    activeSessionsDesc: 'Gérez les appareils et sessions connectés à votre compte',
    currentSession: 'Session Actuelle',
    revokeSession: 'Révoquer',
    revokeSessionConfirm: 'Êtes-vous sûr de vouloir déconnecter cette session?',
    sessionRevoked: 'Session révoquée avec succès',
    noOtherSessions: 'Aucune autre session active',
    dangerZone: 'Zone de Danger',
    deleteAccount: 'Supprimer le Compte',
    deleteAccountDesc: 'Supprimez définitivement votre compte et toutes les données associées. Cette action ne peut pas être annulée.',
    deleteAccountConfirm: 'Êtes-vous absolument sûr de vouloir supprimer votre compte? Cela supprimera définitivement tous vos voyages, itinéraires, documents et préférences. Cette action NE PEUT PAS être annulée.',
    typeToConfirm: 'Tapez DELETE pour confirmer',
    accountDeleted: 'Compte supprimé avec succès',
    deleteAccountError: 'Erreur lors de la suppression du compte',
    createdAt: 'Créée',
    expiresAt: 'Expire',

    // Common
    from: 'De',
    to: 'À',
    date: 'Date',
    cost: 'Coût',
    location: 'Emplacement',
    notes: 'Notes',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    confirm: 'Confirmer',
    optional: '(optionnel)',
    or: 'OU',
    type: 'Type',
    title: 'Titre',

    // Create/Edit Trip Dialog
    createNewTripTitle: 'Créer Nouveau Voyage',
    editTripTitle: 'Modifier Voyage',
    fillDetails: 'Remplissez les détails ci-dessous pour créer un nouveau voyage',
    updateDetails: 'Mettez à jour les détails de votre voyage ci-dessous',
    tripTitle: 'Titre du Voyage',
    tripTitleRequired: 'Titre du Voyage *',
    startingFrom: 'Départ De',
    startingFromRequired: 'Départ De *',
    destinations: 'Destinations',
    destinationsOptional: 'Destinations (Optionnel)',
    searchLocation: 'Rechercher un emplacement (ex., New York, États-Unis)',
    searchDestination: 'Rechercher une destination (ex., Paris, France)',
    startDate: 'Date de Début',
    endDate: 'Date de Fin',
    createTrip: 'Créer Voyage',
    updateTrip: 'Mettre à Jour Voyage',
    deleteConfirm: 'Êtes-vous sûr de vouloir supprimer ce voyage? Cette action ne peut pas être annulée.',
    tripCreatedSuccess: 'Voyage créé avec succès!',
    tripUpdatedSuccess: 'Voyage mis à jour avec succès!',
    tripDeletedSuccess: 'Voyage supprimé avec succès!',
    requiredFields: 'Veuillez remplir les champs requis (Titre et Emplacement de Départ)',
    sessionExpired: 'Session expirée. Veuillez actualiser la page.',
    failedToCreateTrip: 'Échec de la création du voyage',
    failedToUpdateTrip: 'Échec de la mise à jour du voyage',
    failedToDeleteTrip: 'Échec de la suppression du voyage',
    failedToLoadTrips: 'Échec du chargement des voyages',
    loadedFromOffline: 'Voyages chargés depuis le stockage hors ligne',
    sharedWith: 'Partagé avec',
    person: 'personne',
    people: 'personnes',
    currency: 'Devise',

    // Add/Edit Itinerary Dialog
    addItineraryItem: "Ajouter Élément à l\'Itinéraire",
    addItineraryDesc: 'Ajoutez une nouvelle activité, vol, hôtel ou autre élément à votre itinéraire',
    editItineraryItem: "Modifier Élément de l\'Itinéraire",
    editItineraryDesc: "Mettez à jour les détails de cet élément de l\'itinéraire",
    typeHotel: 'Hôtel',
    typeFlight: 'Vol',
    typeTrain: 'Train',
    typeVacationRental: 'Location de Vacances',
    typeCruise: 'Croisière',
    typeActivity: 'Activité',
    typeRestaurant: 'Restaurant',
    typeOther: 'Autre',
    embarkAt: 'Embarquer À',
    debarkAt: 'Débarquer À',
    pleaseEnterBothPorts: "Veuillez entrer les deux ports d\'embarquement et de débarquement",
    embark: 'Embarquer',
    debark: 'Débarquer',
    disembark: 'Débarquer',
    fullRoute: 'Itinéraire Complet',
    confirmation: 'Confirmation',
    portDeparture: 'Départ',
    portArrival: 'Escale',
    searchPortExample: 'Rechercher port (ex., Port de Miami)',
    searchPortArrival: 'Rechercher port (ex., Port Canaveral)',
    typeToSearchPorts: 'Tapez pour rechercher des ports de croisière',
    portsOfCall: 'Escales',
    portsOfCallDesc: 'Ajouter des arrêts intermédiaires sur votre croisière',
    addPort: 'Ajouter un Port',
    noPortsAdded: 'Aucun port ajouté pour le moment',
    portName: 'Nom du Port',
    searchPortPlaceholder: 'ex., Cozumel, Mexique',
    arrivalDate: 'Arrivée',
    departureDate: 'Départ',
    portOfCall: 'Escale',
    cruiseLeg: 'Étape',
    of: 'sur',
    departingFrom: 'Départ De',
    arrivingAt: 'Arrivée À',
    searchAirport: 'Rechercher aéroport',
    searchAirportExample: 'Rechercher aéroport (ex., Los Angeles ou LAX)',
    searchAirportArrival: 'Rechercher aéroport (ex., New York ou JFK)',
    searchStation: 'Rechercher gare',
    searchStationExample: 'Rechercher gare (ex., Paris Gare du Nord)',
    searchStationArrival: 'Rechercher gare (ex., London St Pancras)',
    searchLocationExample: 'Rechercher emplacement (ex., Tour Eiffel, Paris)',
    typeToSearchAirports: 'Tapez pour rechercher des aéroports',
    typeToSearchStations: 'Tapez pour rechercher des gares',
    searchOrEnterLocation: "Rechercher ou entrer l\'emplacement",
    startDateTime: 'Date/Heure de Début',
    endDateTime: 'Date/Heure de Fin',
    confirmationNumber: 'Numéro de Confirmation',
    additionalNotes: 'Ajouter des notes supplémentaires...',
    additionalDetails: 'Détails supplémentaires...',
    saveItem: 'Enregistrer Élément',
    updateItem: 'Mettre à Jour Élément',
    itemAddedSuccess: 'Élément ajouté avec succès!',
    itemUpdatedSuccess: 'Élément mis à jour avec succès!',
    itemDeletedSuccess: 'Élément supprimé',
    failedToAddItem: "Échec de l\'ajout de l\'élément",
    failedToUpdateItem: "Échec de la mise à jour de l\'élément",
    failedToDeleteItem: "Échec de la suppression de l\'élément",
    enterBothAirports: "Veuillez entrer les aéroports de départ et d\'arrivée",
    enterBothStations: "Veuillez entrer les gares de départ et d\'arrivée",
    deleteItemConfirm: 'Êtes-vous sûr de vouloir supprimer cet élément?',

    // Share Dialog
    shareTripTitle: 'Partager Voyage',
    shareTripDesc: 'Partagez ce voyage avec des amis ou des membres de la famille',
    emailAddress: 'Adresse E-mail',
    emailPlaceholder: 'ami@exemple.com',
    tripSharedSuccess: 'Voyage partagé avec succès!',
    failedToShareTrip: 'Échec du partage du voyage',
    enterEmailAddress: 'Veuillez entrer une adresse e-mail',

    // Gmail Connection Dialog
    gmailIntegrationTitle: 'Intégration Gmail',
    gmailIntegrationDesc: 'Connectez Gmail pour importer automatiquement les confirmations de réservation',
    gmailConnected: 'Gmail Connecté',
    notConnected: 'Non Connecté',
    accountLabel: 'Compte',
    features: 'Fonctionnalités',
    featureAutoCheck: 'Vérification automatique des e-mails toutes les 5 minutes',
    featureExtract: 'Extrait vols, hôtels, restaurants et plus',
    featureAIPowered: 'Extraction des détails de réservation par IA',
    featureMarkRead: 'Marque les e-mails traités comme lus',
    connectGmail: 'Connecter Gmail',
    disconnectGmail: 'Déconnecter Gmail',
    checkEmailsNow: 'Vérifier E-mails Maintenant',
    checking: 'Vérification...',
    disconnectGmailConfirm: "Êtes-vous sûr de vouloir déconnecter Gmail? Vous ne recevrez plus d\'importations automatiques d\'e-mails.",
    gmailDisconnectedSuccess: 'Gmail déconnecté avec succès',
    gmailConnectedSuccessfully: 'Gmail connecté avec succès !',
    gmailConnectionFailed: 'Échec de la connexion Gmail. Veuillez réessayer.',
    failedToDisconnectGmail: 'Échec de la déconnexion de Gmail',
    failedToCheckGmailStatus: 'Échec de la vérification du statut de connexion Gmail',
    foundBookingEmails: '{count} e-mail(s) de réservation trouvé(s)!',
    noNewBookingEmails: 'Aucun nouvel e-mail de réservation trouvé',
    failedToFetchEmails: 'Échec de la récupération des e-mails',
    gmailSecurityInfo: 'Vos identifiants Gmail sont stockés en sécurité et jamais partagés',

    // Pass Portfolio Dialog
    passPortfolioTitle: 'Portfolio de Passes',
    offlineAvailable: 'Disponible Hors Ligne',
    passPortfolioDesc: 'Stockez et gérez tous les documents de votre voyage: billets, confirmations et réservations',
    clickToUploadDocs: 'Cliquez pour télécharger des documents',
    uploading: 'Téléchargement...',
    fileTypes: 'PDF, PNG, JPG (Max 5Mo par fichier)',
    noDocumentsYet: 'Pas encore de documents',
    uploadDocsOffline: 'Téléchargez vos documents de voyage pour y accéder à tout moment, même hors ligne!',
    documentsAvailableOffline: '{count} document(s) disponible(s) hors ligne',
    documentDeleted: 'Document supprimé',
    documentDownloaded: 'Document téléchargé',
    failedToDeleteDoc: 'Échec de la suppression du document',
    failedToDownloadDoc: 'Échec du téléchargement du document',
    fileTooLarge: '{name} est trop volumineux. La taille maximale est de 5Mo.',
    uploadedSuccess: '{name} téléchargé avec succès',
    failedToUpload: 'Échec du téléchargement de {name}',

    // Pass Portfolio Tabs
    boardingPasses: "Cartes d\'Embarquement",
    eventTickets: "Billets d\'Événements",
    passportsVisas: 'Passeports et IDs',
    otherDocs: 'Autres Documents',
    noBoardingPasses: "Pas encore de cartes d\'embarquement",
    uploadBoardingDesc: "Téléchargez vos cartes d\'embarquement et billets électroniques",
    noEventTickets: "Pas encore de billets d\'événements",
    uploadTicketsDesc: 'Téléchargez des billets pour les visites, musées, concerts et événements',
    noPassports: 'Pas encore de documents de passeport',
    uploadPassportsDesc: "Stockez les copies de passeports, visas et documents d\'identité pour tous les voyageurs",
    noOtherDocs: "Pas encore d\'autres documents",
    uploadOtherDesc: "Stockez les confirmations d\'hôtel, assurances et autres documents de voyage",
    dropFilesHere: 'Déposez les fichiers ici ou cliquez pour télécharger',
    fileTypesExtended: "PDF, PNG, JPG jusqu\'à 10Mo • Les fichiers se catégorisent automatiquement par nom",
    portfolioSummary: 'Résumé du Portfolio',

    // Email Parse Dialog
    aiEmailParseTitle: "Analyse d\'E-mail par IA",
    aiEmailParseDesc: "Collez votre e-mail de confirmation de réservation et laissez l\'IA extraire tous les détails automatiquement",
    pasteEmailText: "Coller le Texte de l\'E-mail",
    pasteEmailPlaceholder: 'Collez votre e-mail de confirmation de réservation ici...',
    uploadEmailFile: 'Télécharger Fichier E-mail (.txt, .eml)',
    clickToUploadFile: 'Cliquez pour télécharger un fichier',
    supportedFileTypes: 'Fichiers TXT, EML, HTML pris en charge',
    extractBookings: 'Extraire Réservations',
    parsingWithAI: 'Analyse par IA...',
    foundBookings: '{count} réservation(s) trouvée(s)',
    reviewAndAdd: 'Révisez et ajoutez-les à votre itinéraire',
    addToItinerary: "Ajouter à l\'Itinéraire",
    addAllToItinerary: "Tout Ajouter à l\'Itinéraire",
    addingAll: 'Ajout en cours...',
    skip: 'Passer',
    parseAnotherEmail: 'Analyser un Autre E-mail',
    pleaseEnterEmail: "Veuillez coller le texte de l\'e-mail",
    fileLoadedSuccess: 'Fichier chargé avec succès',
    failedToReadFile: 'Échec de la lecture du fichier',
    failedToParseEmail: "Échec de l\'analyse de l\'e-mail",
    noBookingsFound: "Aucune confirmation de réservation trouvée dans l\'e-mail",
    emailParsedSuccess: "E-mail analysé et ajouté à l\'itinéraire!",
    addedToItinerary: "{title} ajouté à l\'itinéraire",
    failedToAddBooking: "Échec de l\'ajout de la réservation",

    // Destination Info Window
    loadingWeather: 'Chargement des prévisions météo...',
    noDatesSet: 'Aucune date définie',
    saveDatesGetWeather: 'Enregistrer Dates et Obtenir Météo',
    selectBothDates: 'Veuillez sélectionner les dates de début et de fin',
    datesSavedWeatherLoaded: 'Dates enregistrées et météo chargée!',
    failedToFetchWeather: 'Échec de la récupération de la météo',
    unableToGetCoordinates: "Impossible d\'obtenir les coordonnées pour la météo",
    photos: 'Photos',
    photosCount: '{count} photos',
    scrollForMore: 'Faites défiler pour plus',
    clickToViewFullSize: 'Cliquez pour voir en taille réelle',
    yourPlans: 'Mes Plans',
    topRestaurants: 'Meilleurs Restaurants',
    topAttractions: 'Meilleures Attractions',

    // Trip Detail Misc
    homebase: 'Base de Départ',
    destinationCity: 'Ville de Destination',
    recentReviews: 'Avis Récents',
    seeAllReviewsOnGoogle: 'Voir tous les avis sur Google Maps',
    otherLocations: 'Autres Emplacements',
    loadedTripOffline: 'Voyage chargé depuis le stockage hors ligne',
    loadedItemsOffline: "{count} éléments d\'itinéraire chargés depuis le stockage hors ligne",
    loadedDocsOffline: 'Documents chargés depuis le stockage hors ligne',
    failedToLoadTrip: 'Échec du chargement du voyage',
    failedToLoadItinerary: "Échec du chargement de l\'itinéraire",
    failedToSaveOrder: "Échec de l\'enregistrement du nouvel ordre",
    failedToLoadPlaceDetails: 'Échec du chargement des détails du lieu',
    openingAddItemDialog: "Ouverture du dialogue d\'ajout d\'élément",
    restaurant: 'Restaurant',
    attraction: 'Attraction',

    // Item Type Names
    itemTypeHotel: 'Hôtel',
    itemTypeRestaurant: 'Restaurant',
    itemTypeActivity: 'Activité',
    itemTypeTrain: 'Train',
    itemTypeOther: "Élément d\'Itinéraire",

    // Mobile Header Actions
    share: 'Partager',
    docs: 'Docs',
    gmail: 'Gmail',
    apps: 'Apps',
    appServices: "Services d\'Apps",
    appServicesDesc: "Gérez vos services d\'apps connectés",
    appIntegrations: "Intégrations d\'Apps",
    appIntegrationsDesc: "Gérez vos intégrations d\'apps connectées",
    aiParse: 'IA Parser',

    // Google Services Dialog
    googleServices: 'Services Google',
    googleServicesDesc: 'Gérez vos services Google connectés',
    googleCalendar: 'Google Calendar',
    calendarServiceDesc: "Exporter les éléments d\'itinéraire vers le calendrier",
    gmailServiceDesc: 'Importer automatiquement les confirmations de réservation',
    googleWallet: 'Google Wallet',
    walletServiceDesc: "Stocker les cartes d\'embarquement et billets",
    serviceDisconnected: 'Services Google déconnectés',
    failedToToggleService: 'Échec de la mise à jour de la connexion au service',
    failedToLoadServices: "Échec du chargement de l\'état des services",
    googleServicesNote: "Gmail et Calendar partagent la même connexion. Déconnecter l\'un déconnectera les deux.",
    manageInGoogle: 'Gérer les autorisations dans le compte Google',

    // Map Markers & Locations
    clickedLocation: 'Emplacement Cliqué',
    searchResult: 'Résultat de Recherche',
    unableToLocateDestination: 'Impossible de localiser la destination sur la carte',
    unableToLocateHomebase: 'Impossible de localiser la base sur la carte',
    destinationsReordered: 'Destinations réorganisées !',
    failedToReorderDestinations: 'Échec de la réorganisation des destinations',

    // Map Actions
    addMarker: 'Ajouter un Marqueur',
    viewOnGoogleMaps: 'Voir sur Google Maps',
    getDirections: "Obtenir l\'Itinéraire",
    addToItineraryShort: "Ajouter à l\'Itinéraire",

    // Weather
    weatherForecast: 'Prévisions Météo',
    high: 'Max',
    low: 'Min',
    precipitation: 'Précipitations',

    // Trip Summary
    tripSummary: 'Résumé du Voyage',
    aiTripSummaryTitle: 'Résumé de Voyage par IA',
    addItemsForSummary: 'Ajoutez des éléments à votre itinéraire pour voir un résumé généré par IA',
    generatingSummary: 'Génération du résumé de votre voyage...',
    tryAgainButton: 'Réessayer',
    failedToGenerateSummary: 'Échec de la génération du résumé du voyage',
    duration: 'Durée',
    days: 'jours',
    day: 'jour',
    totalBudget: 'Budget Total',
    remaining: 'restant',
    overBudget: 'dépassement',

    // Place Details
    openNow: 'Ouvert maintenant',
    closedNow: 'Fermé',
    viewHours: 'Voir les horaires',

    // General UI
    loading: 'Chargement...',
    error: 'Erreur',
    retry: 'Réessayer',
    noResults: 'Aucun résultat trouvé',
    searchPlaceholder: 'Rechercher...',

    // Button Titles & Tooltips
    backToDashboard: 'Retour au Tableau de Bord',
    clickToEditDates: 'Cliquez pour modifier les dates du voyage',
    clickToAddDates: 'Cliquez pour ajouter des dates',
    clickToAddBudget: 'Cliquez pour ajouter un budget',
    clickToSetBudget: 'Cliquez pour définir un budget',
    clickToEditBudget: 'Cliquez pour modifier le budget',
    clickToChangeDates: 'Cliquez pour changer les dates',
    clickToSetDates: 'Cliquez pour définir les dates',
    setDates: 'Définir les dates',
    availableOffline: 'Disponible hors ligne',
    offline: 'Hors ligne',
    addDates: 'Ajouter des dates',
    addBudget: 'Ajouter un budget',
    homebaseTooltip: 'Base: {{location}} (cliquez pour voir sur la carte)',
    searchPlacesActivities: 'Rechercher des lieux, restaurants, activités...',
    tripTitlePlaceholder: 'Aventure Européenne 2024',
    itemTitlePlaceholder: 'ex., Visite de la Tour Eiffel',
    costPlaceholder: 'ex., 150',
    confirmationPlaceholder: 'ex., ABC123',
    budgetPlaceholder: '5000',
    datePlaceholder: 'AAAA-MM-JJ',
    viewTrip: 'Voir le voyage',

    // AI Assist
    aiAssist: 'Assistant IA',
    aiAssistDesc: "Votre assistant de planification de voyage alimenté par l\'IA",
    tripSuggestions: 'Suggestions de Voyage',
    tripSuggestionsDesc: 'Obtenez des recommandations IA pour les activités, restaurants et attractions',
    itineraryOptimization: "Optimisation de l\'Itinéraire",
    itineraryOptimizationDesc: 'Optimisez votre emploi du temps pour une meilleure gestion du temps et logistique',
    budgetRecommendations: 'Recommandations Budget',
    budgetRecommendationsDesc: "Obtenez des conseils d\'économie et des alternatives économiques",
    packingList: 'Liste de Bagages',
    packingListDesc: 'Générez une liste de bagages complète pour votre voyage',
    tripChat: "Posez n\'importe quelle Question",
    tripChatDesc: "Discutez avec l\'IA à propos de votre voyage - posez n\'importe quelle question!",
    askAboutTrip: "Posez n\'importe quelle question sur votre voyage...",
    thinking: 'Réflexion...',
    askAI: "Demander à l\'IA",
    aiGenerating: "L\'IA génère votre réponse...",
    followUpQuestion: 'Posez une question de suivi...',
    askFollowUp: 'Question de Suivi',

    // Flight/Train Badges
    departure: 'Départ',
    arrival: 'Arrivée',
    returnTo: 'Retour à',

    // InfoWindow Marker Types
    departureFlight: 'Vol de Départ',
    arrivalFlight: "Vol d\'Arrivée",
    departureTrain: 'Train de Départ',
    arrivalTrain: "Train d\'Arrivée",
    embarkationPort: "Port d\'Embarquement",
    disembarkationPort: 'Port de Débarquement',
    searchResultMarker: 'Résultat de Recherche',
    clickedLocationMarker: 'Emplacement Sélectionné',
    accommodation: 'Hébergement',

    // Marker Clustering
    tapToExpand: 'Appuyez pour agrandir',
    hoverToExpand: 'Survolez pour agrandir',
    departuresCount: '{count} départs',
    arrivalsCount: '{count} arrivées',

    // Document Actions (PassPortfolioDialog)
    preview: 'Aperçu',
    download: 'Télécharger',

    // Itinerary Item Labels
    aiParsed: 'Analysé par IA',

    // AI Assist Toast Messages
    failedToGenerateAI: 'Échec de la génération de la réponse IA',
    failedToConnectAI: 'Échec de la connexion au service IA',
    pleaseEnterMessage: 'Veuillez entrer un message',
    copiedToClipboard: 'Copié dans le presse-papiers!',

    // Dialog Accessibility Labels

    // Budget Categories

    // Network Status
    youAreCurrentlyOffline: 'Vous êtes actuellement hors ligne',
    changesWillBeSaved: 'Les modifications seront enregistrées lors de la reconnexion',
    backOnline: 'De nouveau en ligne',
    connected: 'Connecté',

    // Error Boundary
    somethingWentWrong: "Une erreur s\'est produite",
    oopsSomethingWentWrong: "Oups! Une erreur s\'est produite",
    unexpectedErrorMessage: 'Nous avons rencontré une erreur inattendue. Ne vous inquiétez pas, vos données sont en sécurité.',
    goHome: "Retour à l\'accueil",

    // Itinerary
    noItineraryItemsYet: "Pas encore d\'éléments dans l\'itinéraire",
    addYourFirstItem: 'Ajoutez votre premier élément',

    // Trip Info
    noDestinationsAdded: 'Aucune destination ajoutée',
    thisIsOneOfYourTripDestinations: "C\'est l\'une des destinations de votre voyage",

    // Pagination
    previous: 'Précédent',
    next: 'Suivant',
    morePages: 'Plus de pages',
    goToPreviousPage: 'Aller à la page précédente',
    goToNextPage: 'Aller à la page suivante',

    // Carousel
    previousSlide: 'Diapositive précédente',
    nextSlide: 'Diapositive suivante',

    // Keyboard Shortcuts
    ctrl: 'Ctrl',
    enter: 'Entrée',

    // Network/Status
    retrying: 'Nouvelle tentative...',
    online: 'En ligne',

    // Empty States
    noItineraryItemsYetParseEmail: "Pas encore d\'éléments dans l\'itinéraire. Ajoutez votre premier élément ou analysez un e-mail!",

    // Common Actions

    // Error Messages
    oops: 'Oups!',
    tryAgain: 'Réessayer',
    networkError: 'Impossible de se connecter. Veuillez vérifier votre connexion internet.',
    serverError: 'Le serveur est temporairement indisponible. Veuillez réessayer plus tard.',
    notFound: "La ressource demandée n\'a pas été trouvée.",
    unauthorized: 'Veuillez vous connecter pour continuer.',
    forbidden: "Vous n\'avez pas la permission d\'accéder à cette ressource.",

    // Toast Messages - Errors
    errorRevokingSession: 'Erreur lors de la révocation de la session',

    // Toast Messages - Success

    // Toast Messages - Info
    loadedFromOfflineStorage: 'Chargé depuis le stockage hors ligne',
    loadedTripsOffline: '{count} voyages chargés depuis le stockage hors ligne',

    // Form Validation
    pleasePasteEmailText: "Veuillez coller le texte de l\'e-mail",
    pleaseFillRequiredFields: 'Veuillez remplir les champs requis (Titre et Lieu de départ)',

    // Placeholders
    enterDisplayName: "Entrez le nom d\'affichage",
    typeDelete: 'SUPPRIMER',

    // Clipboard & Actions

    // Gmail/Email Parsing
    noTravelBookingsFound: 'Aucune réservation de voyage trouvée dans cet e-mail',
    failedToParseEmailManual: "Échec de l\'analyse de l\'e-mail. Veuillez essayer l\'option manuelle Importer une réservation.",
    addedBookingsToItinerary: "{count} réservation(s) ajoutée(s) à l\'itinéraire",
    failedToAddBookings: "Échec de l\'ajout de {count} réservation(s)",

    // Share Link Feature
    sharedTrip: 'Voyage Partagé',
    tripNotFound: 'Voyage Non Trouvé',
    poweredBy: 'Propulsé par',
    planYourOwnTrip: 'Planifiez votre propre voyage',
    unscheduled: 'Non planifié',
    shareViaLink: 'Partager par Lien',
    generateShareLink: 'Générer un Lien',
    shareLinkGenerated: 'Lien généré!',
    copyShareLink: 'Copier le Lien',
    disableSharing: 'Désactiver le Partage',
    sharingDisabled: 'Le partage a été désactivé',
    shareThisTrip: 'Partager ce voyage',
    anyoneWithLink: 'Toute personne avec le lien peut voir ce voyage (lecture seule)',
    shareLinkActive: 'Le lien de partage est actif',
    noShareLinkYet: 'Aucun lien de partage encore généré',
  },
};

export const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage || 'en';
  });

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const t = (key, params = {}) => {
    let text = translations[language]?.[key] || translations['en'][key] || key;
    
    // Handle parameter substitution for strings like "Found {count} booking(s)"
    Object.keys(params).forEach(param => {
      text = text.replace(`{${param}}`, params[param]);
    });
    
    return text;
  };

  const value = {
    language,
    changeLanguage,
    t,
    availableLanguages: AVAILABLE_LANGUAGES,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
